/* ¿Peligra una hora fija? Solo se calcula con lo que marca el autor: si marca «hecho» una parada
   bastante después de su hora de fin, el resto del día se proyecta con ese retraso. Sin marcas
   no hay aviso (mejor ninguno que uno falso). */
import type { BloqueV } from '../escena/bloques.ts';
import type { Marca } from './apuntes.ts';

export interface Peligro {
  ancla: BloqueV;          // la hora fija que peligra (reserva, entrada, examen…)
  llegas: number;          // minuto al que se llegaría con el retraso
  tarde: number;           // minutos de más sobre su hora
  retraso: number;         // cuánto tarde se terminó la última parada marcada
  saltables: BloqueV[];    // qué saltar (C primero, luego B) para llegar a tiempo
}

const MARGEN = 10;          // terminar 10 minutos tarde no es ir tarde
const fija = (b: BloqueV) => /FIJO|CONFIRMADO/.test(b.modo);
const dur = (b: BloqueV) => Math.max(0, Math.min(b.t.fin, b.t.ini + 300) - b.t.ini);

export function peligro(bl: BloqueV[], dia: number, marca: (id: string) => Marca | null): Peligro | null {
  // La última parada marcada como hecha hoy, con su minuto
  let ultima: BloqueV | undefined, cuando = 0;
  for (const b of bl) {
    const m = marca(b.id);
    if (m?.estado === 'hecho' && m.min != null && m.dia === dia && (!ultima || b.t.ini > ultima.t.ini)) { ultima = b; cuando = m.min; }
  }
  if (!ultima) return null;
  const retraso = cuando - ultima.t.fin;
  if (retraso <= MARGEN) return null;

  // Lo que queda hasta la siguiente hora fija, sin lo hecho ni lo saltado
  const despues = bl.filter(b => b.t.ini > ultima!.t.ini && !marca(b.id));
  const ancla = despues.find(fija);
  if (!ancla) return null;
  const antes = despues.filter(b => b !== ancla && b.t.ini < ancla.t.ini && !b.examen);

  // Se recorre el día con el retraso: cada parada empieza cuando toca o cuando acaba la anterior
  const proyectar = (lista: BloqueV[]) => {
    let fin = cuando;
    for (const b of lista) fin = Math.max(b.t.ini, fin) + dur(b);
    return Math.max(ancla.t.ini, fin);
  };
  const llegas = proyectar(antes);
  const tarde = llegas - ancla.t.ini;
  if (tarde <= 5) return null;

  // Qué saltar: primero lo que «cae primero» (C), luego lo importante (B); lo protegido (A), nunca
  const saltables: BloqueV[] = [];
  let quedan = [...antes];
  for (const pri of ['C', 'B']) {
    for (const b of antes.filter(x => x.pri === pri && x.tipo !== 'traslado')) {
      if (proyectar(quedan) - ancla.t.ini <= 5) break;
      saltables.push(b);
      quedan = quedan.filter(x => x !== b);
    }
  }
  return { ancla, llegas, tarde, retraso, saltables: proyectar(quedan) - ancla.t.ini <= 5 ? saltables : [] };
}
